import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Boxes, AlertTriangle, Warehouse } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { fetchConsoleStock, type ConsoleStockRow } from "@/lib/djayma";

// ---------------------------------------------------------------------------
// DJAYMA Stock console (Epic 1) — warehouse availability view over the djayma
// engine: net available units per warehouse × SKU with a reorder flag. Reads
// are scoped by RLS; the reorder threshold drives the "Réappro" status badge.
// ---------------------------------------------------------------------------

function Kpi({
  icon,
  label,
  value,
  tone,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string | number;
  readonly tone: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</div>
        <div>
          <p className="text-2xl font-semibold text-stone-900">{value}</p>
          <p className="text-sm text-stone-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function StockConsole() {
  const q = useQuery({ queryKey: ["djayma", "console-stock"], queryFn: () => fetchConsoleStock(), retry: false });

  const rows: ConsoleStockRow[] = q.data ?? [];
  const lowCount = rows.reduce((acc, r) => acc + (r.low ? 1 : 0), 0);
  const totalAvailable = rows.reduce((acc, r) => acc + Number(r.available ?? 0), 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Warehouse className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Stock — disponibilité</h1>
            <p className="text-sm text-stone-500">
              Disponibilité nette par entrepôt et par référence — seuils de réapprovisionnement
            </p>
          </div>
        </div>

        {q.isError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Moteur djayma non joignable — impossible de charger les stocks. Vérifiez les migrations 131-140 et
            l&apos;exposition du schéma <code className="mx-1 rounded bg-amber-100 px-1">djayma</code>.
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi
            icon={<Boxes className="h-5 w-5" />}
            tone="bg-stone-100 text-stone-700"
            label="Lignes SKU"
            value={rows.length}
          />
          <Kpi
            icon={<AlertTriangle className="h-5 w-5" />}
            tone="bg-rose-100 text-rose-700"
            label="À réapprovisionner"
            value={lowCount}
          />
          <Kpi
            icon={<Warehouse className="h-5 w-5" />}
            tone="bg-amber-100 text-amber-700"
            label="Unités disponibles"
            value={totalAvailable}
          />
        </div>

        {/* Stock table */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Disponibilité par référence</h2>
          {rows.length === 0 ? (
            <p className="text-sm text-stone-400">Aucun stock.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-stone-400">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Entrepôt</th>
                    <th className="py-2 pr-4 font-medium">SKU</th>
                    <th className="py-2 pr-4 font-medium">Produit</th>
                    <th className="py-2 pr-4 font-medium">Marque</th>
                    <th className="py-2 pr-4 font-medium text-right">Disponible</th>
                    <th className="py-2 pr-4 font-medium text-right">Seuil réappro</th>
                    <th className="py-2 pr-4 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r) => (
                    <tr key={`${r.warehouse_id}-${r.sku}`}>
                      <td className="py-2 pr-4 text-stone-700">{r.warehouse}</td>
                      <td className="py-2 pr-4 text-stone-500">{r.sku}</td>
                      <td className="py-2 pr-4 text-stone-700">{r.product}</td>
                      <td className="py-2 pr-4 text-stone-500">{r.brand}</td>
                      <td className="py-2 pr-4 text-right font-medium text-stone-800">{r.available}</td>
                      <td className="py-2 pr-4 text-right text-stone-500">{r.reorder_point}</td>
                      <td className="py-2 pr-4">
                        {r.low ? (
                          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">
                            Réappro
                          </span>
                        ) : (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            OK
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
